import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, ArrowRight, Mail, PhoneCall, Sparkles, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InstallPWA } from "@/components/InstallPWA";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG = "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJhbHxlbnwwfHx8fDE3ODE4NTI2ODV8MA&ixlib=rb-4.1.0&q=85";
const DOMAIN = "united-group.in";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-10 max-w-xl mx-auto w-full">
        <div className="flex items-center gap-3 mb-8" data-testid="login-brand">
          <img src="/uvi-emblem.svg" alt="UniVicoustic" className="h-12 w-auto" />
          <span className="h-9 w-px bg-slate-200" />
          <div className="leading-tight">
            <img src="/uvi-wordmark.svg" alt="UniVicoustic" className="h-3.5 w-auto" />
            <p className="text-[11px] text-slate-400 mt-1.5">A United Group Company</p>
          </div>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Univicoustic CRM</h1>
        <p className="text-slate-500 mt-2 text-sm">Sign in to your acoustic sales workspace.</p>

        <div className="mt-5 flex items-start gap-3 rounded-xl border border-brand/20 bg-brand/[0.04] px-4 py-3" data-testid="login-call-promo">
          <span className="h-9 w-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center flex-shrink-0">
            <PhoneCall className="h-[18px] w-[18px]" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              New: AI Daily Call Logs <Sparkles className="h-3.5 w-3.5 text-brand" />
            </p>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Record any sales call (Hindi/English) and let AI write the minutes & set reminders for every action point. Sign in to start logging.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-medium text-slate-700">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
              placeholder={`you@${DOMAIN}`} className="h-10" data-testid="login-email" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs font-medium text-slate-700">Password</Label>
              <button type="button" onClick={() => setShowForgot((v) => !v)} className="text-[11px] text-brand hover:underline" data-testid="login-forgot-link">
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Input id="password" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                placeholder="••••••••" className="h-10 pr-10" data-testid="login-password" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}
                aria-label={showPwd ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-700"
                data-testid="login-password-toggle">
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {showForgot && (
            <p className="text-xs text-slate-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 flex gap-2" data-testid="forgot-password-note">
              <Mail className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
              Password resets are handled by your administrator. Please contact your admin to receive a temporary password — you'll be asked to set a new one on your next sign-in.
            </p>
          )}
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}

          <Button type="submit" disabled={loading} className="w-full h-10 bg-brand hover:bg-brand/90 text-white gap-2" data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </Button>
          <p className="text-[11px] text-slate-400">Accounts are provisioned by your administrator. Contact them if you need access.</p>
        </form>

        <div className="mt-8 border-t border-slate-100 pt-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Install as an app</p>
          <div className="flex items-center gap-3 flex-wrap">
            <InstallPWA />
            <p className="text-xs text-slate-500 leading-relaxed">
              <span className="font-medium text-slate-700">Android (Chrome):</span> tap “Install App” above, or browser menu <span className="font-mono">⋮ → Add to Home screen</span>.<br />
              <span className="font-medium text-slate-700">iPhone (Safari):</span> tap <span className="font-mono">Share ⬆ → Add to Home Screen</span>.
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="flex gap-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand">PET Acoustic Panels</span>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/15 backdrop-blur">VMT Series</span>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/15 backdrop-blur">Acoustic Forms</span>
          </div>
          <h2 className="font-heading text-3xl font-bold leading-tight max-w-md">Sound design, sold smarter.</h2>
          <p className="text-slate-200 mt-3 text-sm max-w-md">Region-aware pipelines, dealer registrations and Sales-Head-approved quotations for Univicoustic — one workspace for your whole team.</p>
        </div>
      </div>
    </div>
  );
}
